import React, { useState } from 'react';
import api from '../../../api';
import { toast } from 'react-toastify';

const StatusManagement = ({ selectedStudentIds, onStatusUpdate, tenantId }) => {
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState('');
  const [statusLoading, setStatusLoading] = useState(false);

  const handleBulkStatusUpdate = async () => {
    if (!selectedStatus || selectedStudentIds.length === 0) return;

    setStatusLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      // FIXED: Added tenantId to request body
      const response = await api.put(
        '/api/students/bulk-update-status',
        {
          studentIds: selectedStudentIds,
          status: selectedStatus,
          tenantId: tenantId // CRITICAL: Add tenantId
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data.success) {
        toast.success(`All ${selectedStudentIds.length} students updated to ${selectedStatus}`);
      } else if (response.data.results) {
        const failed = response.data.results.filter(r => r.status === 'failed');
        if (failed.length === 0) {
          toast.success(`All ${selectedStudentIds.length} students updated to ${selectedStatus}`);
        } else {
          toast.warning(`Updated ${selectedStudentIds.length - failed.length} students, ${failed.length} failed`);
        }
      }

      setShowStatusModal(false);
      setSelectedStatus('');
      if (onStatusUpdate) onStatusUpdate();
    } catch (error) {
      console.error('Bulk status update error:', error);
      toast.error(error.response?.data?.message || 'Failed to update student status');
    } finally {
      setStatusLoading(false);
    }
  };

  if (selectedStudentIds.length === 0) return null;

  return (
    <>
      {/* Bulk Status Actions */}
      {selectedStudentIds.length > 0 && (
        <div className="fixed bottom-6 left-6 z-40">
          <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-4">
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-blue-500 rounded-full animate-pulse"></div>
                <span className="text-sm font-medium text-gray-700">
                  {selectedStudentIds.length} students selected
                </span>
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => {
                    setSelectedStatus('active');
                    setShowStatusModal(true);
                  }}
                  className="px-4 py-2 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white text-sm font-medium rounded-lg transition-all duration-200 shadow-sm flex items-center"
                >
                  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Activate
                </button>
                <button
                  onClick={() => {
                    setSelectedStatus('inactive');
                    setShowStatusModal(true);
                  }}
                  className="px-4 py-2 bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white text-sm font-medium rounded-lg transition-all duration-200 shadow-sm flex items-center"
                >
                  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                  Deactivate
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Status Confirmation Modal */}
      {showStatusModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl p-6 max-w-sm w-full transform transition-all duration-200 scale-100">
            <div className={`flex items-center justify-center w-12 h-12 mx-auto rounded-full mb-4 ${
              selectedStatus === 'active' ? 'bg-green-100' : 'bg-red-100'
            }`}>
              <svg className={`w-6 h-6 ${selectedStatus === 'active' ? 'text-green-600' : 'text-red-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            
            <h2 className="text-lg font-bold text-gray-800 text-center mb-2">
              Confirm Status Update
            </h2>
            <p className="text-gray-600 text-center mb-6">
              Are you sure you want to mark <span className="font-bold">{selectedStudentIds.length}</span> students as{' '}
              <span className={`font-bold ${selectedStatus === 'active' ? 'text-green-600' : 'text-red-600'}`}>
                {selectedStatus}
              </span>?
            </p>
            
            <div className="flex justify-center space-x-3">
              <button
                onClick={() => setShowStatusModal(false)}
                className="px-6 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors duration-200"
                disabled={statusLoading}
              >
                Cancel
              </button>
              <button
                onClick={handleBulkStatusUpdate}
                className={`px-6 py-2 border border-transparent rounded-lg text-sm font-medium text-white ${
                  selectedStatus === 'active' 
                    ? 'bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700' 
                    : 'bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700'
                } flex items-center justify-center transition-all duration-200 shadow-sm`}
                disabled={statusLoading}
              >
                {statusLoading ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Updating...
                  </>
                ) : 'Update Status'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default StatusManagement;